import React, { useEffect, useState } from 'react';
import { useQuery } from '@edreamsodigeo/frontend-api-client';

import { useSearchTrainItineraryQuery } from '../SearchRequestProvider';
import {
  useTrackErrors,
  useUpdateMeshTransactionId,
  useLoadingResultsPage,
  useSkipQueryMetric,
} from './hooks';
import FrontendAPIErrorManager from 'clientState/errorManagement';
import {
  SearchTrainsRequest,
  TrainSearchUnbundleItineraryPriceSegmentQuery,
  TrainSearchUnbundleItineraryPriceSegmentQueryVariables,
} from 'generated/graphql';
import { useEndSearchMetricEffect } from '../SearchTrackerProvider';
import { useFrontendApiBlockingError } from '@edreamsodigeo/frontend-core';
import { transformSuccessQuery } from '../transformSuccessQuery';
import { Extensions } from 'clientState/resolvers/itinerary';
import { useAlternativeItineraryMutation } from 'clientState/providers/AlternativeItineraryMutationProvider';
import { useHash } from 'clientState/hooks/useUrlParams';
import { SearchResponseProviderProps } from './types';
import {
  SearchResponseContext,
  SearchResponseActionsContext,
  replaceItinerariesById,
} from './index';
import { transformSearchItineraryTrainUnbundlePricePerSegmentResponse } from './helpers/transformSearchItineraryTrainUnbundlePricePerSegmentResponse';
import { useSelectedUnbundleDepartureId } from '../SelectedUnbundleDepartureProvider';
import {
  useFrontendApiValidationError,
  VALIDATION_SEARCH_ERROR,
} from 'clientState/hooks/useFrontendApiValidationError/useFrontendApiValidationError';
import { TRAIN_SEARCH_UNBUNDLE_ITINERARY_PRICE_SEGMENT_QUERY } from '../../providers/operations/trainSearchUnbundleItineraryPriceSegmentQuery';
import { transformSearchItineraryTrainResponse } from 'clientState/providers/SearchResponseProvider/helpers/transformSearchItineraryTrainResponse';
import { UnbundleContext } from './SearchItinerariesResponseProvider';

const TrainSearchItinerariesResponseProviderWithUnbundlePricePerSegment: React.FC<
  SearchResponseProviderProps & { isUnbundleEnabled?: boolean }
> = ({
  onNoSearchResults,
  onSearchParamsError,
  children,
  onLoadingResultsPage,
}) => {
  const searchTrainsParamRequest = useSearchTrainItineraryQuery();
  const hash = useHash();
  const isUnbundleState = useState(false);
  const isUnbundlePricePerSegmentState = useState(false);
  // This is true if unbundle is enabled, it's round trip and it's mobile
  const selectedDepartureId = useSelectedUnbundleDepartureId();
  const [isUnbundle, setIsUnbundle] = isUnbundleState;
  const [isUnbundlePricePerSegment, setIsUnbundlePricePerSegment] =
    isUnbundlePricePerSegmentState;
  const {
    alternativeMutationLaunched,
    alternativeSearchId,
    alternativeItineraries,
  } = useAlternativeItineraryMutation();
  const isNotAllowedHash =
    hash &&
    !(hash.includes('#results') || hash.includes('#/results')) &&
    isUnbundle &&
    hash.includes('step=return');
  const skipQuery =
    searchTrainsParamRequest === null ||
    alternativeMutationLaunched ||
    alternativeSearchId !== null ||
    !!isNotAllowedHash;
  useSkipQueryMetric(
    skipQuery,
    searchTrainsParamRequest,
    alternativeMutationLaunched,
    alternativeSearchId,
    isNotAllowedHash
  );
  const queryItinerarySearchResult = useQuery<
    TrainSearchUnbundleItineraryPriceSegmentQuery,
    TrainSearchUnbundleItineraryPriceSegmentQueryVariables
  >(TRAIN_SEARCH_UNBUNDLE_ITINERARY_PRICE_SEGMENT_QUERY, {
    variables: {
      searchTrainsRequest: searchTrainsParamRequest as SearchTrainsRequest,
    },
    skip: skipQuery,
    notifyOnBackgroundFetching: true,
  });
  // Handles any broken flow error from the back-end, but not the validation
  // errors
  useFrontendApiBlockingError(queryItinerarySearchResult, [
    VALIDATION_SEARCH_ERROR,
  ]);
  // Handles the validation errors
  useFrontendApiValidationError(queryItinerarySearchResult);

  const itineraries =
    queryItinerarySearchResult.data?.searchTrains?.itineraries || [];
  const itinerariesWithAlternatives = replaceItinerariesById(
    itineraries,
    alternativeItineraries
  );

  if (!isUnbundle && queryItinerarySearchResult.data) {
    queryItinerarySearchResult.data.searchTrains.itineraries =
      itinerariesWithAlternatives;
  }

  // This use effect is to update isUnbundle flag once we receive
  // the value of the searchRequest
  useEffect(() => {
    setIsUnbundle(searchTrainsParamRequest?.tripType === 'ROUND_TRIP');
    setIsUnbundlePricePerSegment(
      searchTrainsParamRequest?.tripType === 'ROUND_TRIP'
    );
  }, [searchTrainsParamRequest, setIsUnbundle, setIsUnbundlePricePerSegment]);

  const searchQueryState = transformSuccessQuery(
    {
      loading:
        queryItinerarySearchResult.loading ||
        queryItinerarySearchResult.fetching,
      error: queryItinerarySearchResult.errors,
      data: queryItinerarySearchResult.data,
      extensions: queryItinerarySearchResult.extensions,
    },
    isUnbundle
      ? transformSearchItineraryTrainUnbundlePricePerSegmentResponse
      : transformSearchItineraryTrainResponse,
    {
      selectedDepartureId,
    }
  );
  const { loading: loadingSearch, data, error } = searchQueryState;

  const loading =
    loadingSearch ||
    searchTrainsParamRequest === null ||
    queryItinerarySearchResult.fetching ||
    (!error && queryItinerarySearchResult.data === null);
  const extensions = searchQueryState?.extensions;

  useEndSearchMetricEffect(queryItinerarySearchResult);
  useTrackErrors(data, loading, queryItinerarySearchResult.errors);
  useUpdateMeshTransactionId(data);
  useLoadingResultsPage(loading, onLoadingResultsPage);
  const nextQueryState = React.useMemo(
    () => ({
      loading,
      data: loading ? null : data,
      error,
      extensions: extensions as Extensions | null,
    }),
    [loading, data, error, extensions]
  );

  const actions = {
    onNoSearchResults,
    onSearchParamsError,
  };

  return (
    <>
      <FrontendAPIErrorManager
        errors={queryItinerarySearchResult.errors}
        id="SearchResponseProvider"
      />
      <SearchResponseContext.Provider value={nextQueryState}>
        <SearchResponseActionsContext.Provider value={actions}>
          <UnbundleContext.Provider
            value={{ isUnbundle, isUnbundlePricePerSegment }}
          >
            {children}
          </UnbundleContext.Provider>
        </SearchResponseActionsContext.Provider>
      </SearchResponseContext.Provider>
    </>
  );
};

export default TrainSearchItinerariesResponseProviderWithUnbundlePricePerSegment;
